import { motion } from 'framer-motion';
import PrimaryButton from '../global/PrimaryButton';

export default function ContactHero() {
  return (
    <>
      <section className="relative overflow-hidden py-12 lg:py-20">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[100px] opacity-25 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, #0ea5e9 0%, #06b6d4 40%, transparent 70%)' }} />
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] text-sky-300 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-sky-400" /> GET IN TOUCH</div>
            <h1 className="mt-3 text-[36px] lg:text-[52px] font-semibold tracking-[-0.03em] leading-[0.95] text-white">
              About <span className="bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent">Draunt</span>
            </h1>
            <p className="mt-4 text-[17px] leading-7 text-white/55 max-w-2xl mx-auto">We&apos;re a tech company in Washington, USA dedicated to creating great software to improve lives.</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }} className="relative overflow-hidden rounded-[28px] border border-white/10 bg-black/20">
              <img src="/about.webp" alt="About Draunt" className="w-full h-full object-cover min-h-[360px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="glass rounded-2xl p-4">
                  <div className="text-xs tracking-widest text-white/40 font-medium">WASHINGTON, USA</div>
                  <div className="text-sm font-medium text-white mt-1">Building useful software — one app at a time.</div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }} className="glass-strong rounded-[28px] p-8 gradient-border">
              <h2 className="text-[22px] font-semibold tracking-tight text-white">Contact Us</h2>
              <p className="text-[14px] leading-6 text-white/55 mt-2">Have questions or feedback? We&apos;d love to hear from you. Reach out to us at:</p>

              <div className="mt-8 space-y-5">
                <div className="flex items-center gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shrink-0">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Email Us</p>
                    <a href="mailto:mail@draunt.com" className="text-sm text-sky-300 hover:text-sky-200 transition">mail@draunt.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Location</p>
                    <p className="text-sm text-white/60">Washington, USA</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-2xl bg-gradient-to-br from-sky-500/20 to-cyan-500/20 border border-sky-500/20 p-4">
                <div className="text-xs font-semibold text-white">We typically reply within 24 hours</div>
                <div className="text-xs text-white/50 mt-1">For app support, please include your device and iOS version.</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 lg:px-8 pb-20">
        <div className="relative rounded-[32px] overflow-hidden p-[1px] bg-gradient-to-br from-sky-500/40 to-cyan-500/30">
          <div className="relative rounded-[31px] bg-[#0a0a14] px-6 lg:px-12 py-10 text-center">
            <h2 className="text-[24px] lg:text-[28px] font-semibold tracking-tight text-white">Discover our apps</h2>
            <p className="text-white/50 mt-2 max-w-xl mx-auto">Discover our collection of powerful, user-friendly apps designed to make your life easier.</p>
            <div className="mt-6">
              <PrimaryButton as="a" href="/#apps">Explore Our Apps</PrimaryButton>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
